import type {Theme, ThemeOptions} from '@mui/material/styles'
import shadows from './shadow.ts'

export const sharedTheme: ThemeOptions = {
  shadows,
  typography: {
    h1: {
      fontFamily: 'Circular Black',
      fontSize: '2em',
      marginTop: '16px',
      marginBottom: '16px',
    },
    h2: {
      fontFamily: 'Circular Bold',
      fontSize: '1.5em',
    },
    h3: {
      fontFamily: 'Circular Bold',
      fontSize: '1.17em',
    },
    h4: {
      fontFamily: 'Circular Bold',
      fontSize: '1em',
    },
    h5: {
      fontFamily: 'Circular Bold',
      fontSize: '.83em',
    },
    h6: {
      fontFamily: 'Circular Bold',
      fontSize: '.67em',
    },
    body1: {fontFamily: 'Circular Medium'},
    body2: {fontFamily: 'Circular Medium'},
    subtitle1: {fontFamily: 'Circular Book'},
    subtitle2: {fontFamily: 'Circular Book'},
    fontFamily: [
      'Circular Medium',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  shape: {borderRadius: 7},
  components: {
    MuiCard: {
      defaultProps: {
        // No `elevation` here: Paper already defaults to 1, and an explicit one
        // makes MUI warn on every `variant="outlined"` card.
        sx: {
          '& .MuiCardHeader-root': {
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiCardActions-root': {
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        sx: {
          '& .MuiCardHeader-root': {
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiCardActions-root': {
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        // AB#5434: a solid #000000 fill has no visible edge against an already-dark surface
        // (e.g. the Digital Twin graph canvas) -- add a theme-aware border so the tooltip reads
        // as a distinct box instead of disappearing into the background it's floating over.
        tooltip: ({theme}) => ({
          backgroundColor: '#000000',
          borderRadius: '3px',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'}`,
        }),
        arrow: ({theme}) => ({
          '&::before': {
            backgroundColor: '#000000',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'}`,
          },
        }),
      },
      defaultProps: {arrow: true},
    },
    MuiButton: {
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
          textTransform: 'capitalize',
        },
      },
      defaultProps: {disableElevation: true},
    },
    // Zeroing only mb left the default 14px top margin on the "Rows per page"/
    // "x of y" <p> labels, pushing them below the toolbar's vertical center
    // relative to the unmargined rows-per-page Select next to them.
    MuiTablePagination: {defaultProps: {sx: {'& p': {m: 0}}}},
    MuiDialogTitle: {styleOverrides: {root: {fontSize: '1.2em'}}},
    MuiTab: {styleOverrides: {root: {textTransform: 'none'}}},
    MuiCssBaseline: {
      styleOverrides: (themeParam: Theme) => `
			 html, body, #root {
				 height: 100%;
				 overflow: hidden;
			}
            /* A closed overlay must never take clicks, even while it is still in
               the DOM. MUI's ModalManager removes aria-hidden from a Modal root
               when the overlay opens and puts it back the moment it closes (add
               / remove), independently of the exit transition — so this selector
               means exactly "closed, or covered by a modal above it", and in
               both cases the node has no business answering the mouse.
               Without this, an overlay whose exit transition never reports back
               stays mounted at position:fixed; inset:0; z-index:1300 with an
               invisible backdrop and silently swallows every click on the page
               until a reload (AB#6086, AB#6069, AB#5784, AB#5750).
               useOverlayMount unmounts the overlays we have reached; this is the
               floor under the ones we have not. A Dialog is the exception — it
               only sets aria-hidden from the very exit callback it can lose, so
               Dialogs rely on useOverlayMount (BasicModal) instead. */
            .MuiModal-root[aria-hidden='true'] {
                pointer-events: none;
            }
            /* Exposed as custom properties too (AB#6195), not just used
               inline below -- index.css's Teams-cascade-race fix reads these
               instead of duplicating the color values, so both places stay
               driven by this one theme computation. */
            :root {
                --mbai-link-color: ${themeParam.palette.primary.main};
                --mbai-link-hover-color: ${themeParam.palette.secondary.main};
            }
            a {
            	text-decoration: none;
                color: var(--mbai-link-color);
            }
            a:hover {
                color: var(--mbai-link-hover-color);
            }
            input::-webkit-credentials-auto-fill-button {
                background-color: ${themeParam.palette.primary.main} !important;
            }
            .introjs-tooltipReferenceLayer * {
                font-family: ${themeParam.typography.fontFamily};
            }
            .mbaiTutorialTooltip {
                background-color: ${themeParam.palette.background.default};
                overflow: hidden;
            }
            .introjs-tooltip-header {
                border-bottom-width: 1px;
                border-bottom-style: solid;
                border-bottom-color: ${themeParam.palette.divider};
                padding-bottom: 12px;
                background-color: ${themeParam.palette.background.default};
            }
            .introjs-tooltipbuttons {
                border-top-width: 1px;
                border-top-style: solid;
                border-top-color: ${themeParam.palette.divider};
            }
            .introjs-prevbutton,
            .introjs-nextbutton {
                padding: 2px 16px;
                border: 1px solid ${themeParam.palette.primary.main};
                border-radius: 7px;
            }
            .introjs-prevbutton.introjs-disabled,
            .introjs-nextbutton.introjs-disabled {
                background: transparent;
                border: 1px solid ${themeParam.palette.divider};
            },
            * {
                scrollbar-width: thin;
                scrollbar-color: ${themeParam.palette.background.scrollbar} transparent;
            }
            *::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }
            *::-webkit-scrollbar-thumb {
                background-color: ${themeParam.palette.background.scrollbar};
                border-radius: 8px;
            }
            *::-webkit-scrollbar-track {
                background-color: transparent;
            }
          `,
    },
  },
}
